import React, { useMemo, useState } from 'react'
import { browserHistory } from 'react-router'
import { Button, Input, Table, Tag, Tooltip, Typography } from 'antd'
import {
  ArrowRightOutlined,
  LockOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useRolesQuery } from '../../hooks/useRolesQuery'
import type { RoleDto } from '../../api/access-control.client'
import { ACCESS_CONTROL_ROLE_DETAIL } from '../../paths'
import { isLockedRole } from '../../locked-roles'
import StatusBadge from '../../components/StatusBadge'
import shared from '../../components/AccessControl.styles.module.scss'
import styles from './RolesList.module.scss'

const { Text } = Typography

function navigate(roleId: string) {
  browserHistory.push(ACCESS_CONTROL_ROLE_DETAIL.replace(':roleId', roleId))
}

const columns: ColumnsType<RoleDto> = [
  {
    title: 'Role',
    key: 'name',
    render: (_, role) => (
      <div className={styles.roleCell}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Text className={styles.roleName}>{role.name}</Text>
          {isLockedRole(role.name) && (
            <Tooltip title="This role's permissions are locked and can't be edited">
              <Tag
                icon={<LockOutlined />}
                color="default"
                style={{ marginInlineEnd: 0 }}
              >
                Locked
              </Tag>
            </Tooltip>
          )}
        </span>
        {role.description && (
          <Text className={styles.roleDesc}>{role.description}</Text>
        )}
      </div>
    ),
  },
  {
    title: 'Users',
    dataIndex: 'userCount',
    key: 'userCount',
    width: 90,
    align: 'center' as const,
    render: (count: number) => count ?? 0,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 130,
    render: (status: string) => <StatusBadge status={status} />,
  },
  {
    title: 'Created',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 160,
    render: (v: string) => dayjs(v).format('MMM D, YYYY'),
  },
  {
    title: '',
    key: 'action',
    width: 56,
    align: 'right' as const,
    render: (_, role) => (
      <Button
        type="text"
        size="small"
        icon={<ArrowRightOutlined />}
        onClick={(e) => {
          e.stopPropagation()
          navigate(role.id)
        }}
        className={styles.arrowBtn}
      />
    ),
  },
]

export default function RolesList() {
  const { data: roles = [], isLoading } = useRolesQuery()
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim()
    if (!q) return roles
    return roles.filter((r) =>
      `${r.name} ${r.description ?? ''}`.toLowerCase().includes(q)
    )
  }, [roles, search])

  return (
    <div>
      <div className={shared.filterBar}>
        <Input
          placeholder="Search roles…"
          prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`${shared.searchInput} ${styles.search}`}
          allowClear
        />
      </div>

      <p className={shared.countText}>
        Showing <span>{filtered.length}</span> of <span>{roles.length}</span>{' '}
        role{roles.length !== 1 ? 's' : ''}
      </p>

      <div className={shared.plainTable}>
        <Table
          rowKey="id"
          loading={isLoading}
          dataSource={filtered}
          columns={columns}
          pagination={{ pageSize: 10, hideOnSinglePage: true, size: 'small' }}
          onRow={(role) => ({
            onClick: () => navigate(role.id),
            style: { cursor: 'pointer' },
          })}
        />
      </div>
    </div>
  )
}
